import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeCategory = categoryMap[slug || ''] || categories[0];
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const categoryArticles = useMemo(() => getArticlesByCategory(activeCategory.slug), [activeCategory.slug]);
  const totalPages = Math.ceil(categoryArticles.length / pageSize);
  const pagedArticles = categoryArticles.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baoliaowang.com' },
      { '@type': 'ListItem', position: 2, name: activeCategory.name, item: `https://baoliaowang.com/category/${activeCategory.slug}` },
    ],
  };

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 爆料网`,
    description: activeCategory.description,
    url: `https://baoliaowang.com/category/${activeCategory.slug}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} - 爆料网`}
        description={activeCategory.description}
        keywords={`爆料网,${activeCategory.name},独家爆料,内幕资讯`}
        canonicalUrl={`https://baoliaowang.com/category/${activeCategory.slug}`}
      />
      <SchemaOrg schema={breadcrumbSchema} />
      <SchemaOrg schema={collectionSchema} />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: activeCategory.name }]} />
            <div className="flex items-center gap-3 pb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className={`${activeCategory.icon} text-lg`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {activeCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{activeCategory.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setCurrentPage(1)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory.slug === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-5">
                  {pagedArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="horizontal" />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                          currentPage === page
                            ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
    </>
  );
}